import {useState, useCallback} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import useShow from '../../../../hooks/useShow';
import useTimeout from '../../../../hooks/useTimeout';
import {TSupportVideos} from '../../../../redux/management/management.type';
import {TShow} from '../../../../component/prompotionPopup/PromotionPopup';
import {trackEvent} from '../../../../utils/analytics';
import useRouteInfo from '../../hooks/useRouteInfo';
import { incrementVideoViews } from '../../../../redux/management/managementActions';

// Popup types - add here when adding a new popup
export type PopupType = 'videosList' | 'videoDetails' | 'videoForm' | 'popupRating';

// Data for each popup type
interface VideoDetailsData {
    video: TSupportVideos | null;
    relatedVideos: TSupportVideos[];
    pageType: string;
}

interface VideoFormData {
    videoId: number | null;
    pageType: string;
}

interface PopupRatingData {
    pageType: string;
}

interface UsePopupsVisibilityParams {
    showVideos: TShow;
    openWidget: (e?: React.MouseEvent) => void;
    setExpandedVideoId: (id: number) => void;
}

interface UsePopupsVisibilityReturn {
    // States
    popupsShowState: {
        videosList: TShow;
        videoDetails: TShow & { data: VideoDetailsData };
        videoForm: TShow & { data: VideoFormData };
        popupRating: TShow & { data: PopupRatingData };
    };
    
    // Actions - videos list
    closeVideosList: () => Promise<void>;
    
    // Actions - video details
    openVideoDetails: (video: TSupportVideos, relatedVideos?: TSupportVideos[], pageType?: string) => Promise<void>;
    closeVideoDetails: () => void;
    switchVideo: (video: TSupportVideos) => void;
    
    // Actions - edit form
    openVideoForm: (videoId: number | null, pageType: string) => void;
    openFormFromDetails: () => void;
    closeVideoForm: () => void;
    
    // Actions - rating
    openPopupRating: (pageType?: string) => void;
    closePopupRating: () => void;
    openFormFromRating: (videoId: number) => void;
    
    // Actions - general
    openContactSupport: (e?: React.MouseEvent) => void;
    closeAll: () => void;
}

const ANIMATION_DELAY = 300;

export function usePopupsVisibility({showVideos, openWidget, setExpandedVideoId}: UsePopupsVisibilityParams): UsePopupsVisibilityReturn {
    const navigate = useNavigate();
    const location = useLocation();
    const {pageType, routeType, id} = useRouteInfo();

    // Video Details state
    const videoDetailsShow = useShow();
    const [detailsData, setDetailsData] = useState<VideoDetailsData>({
        video: null,
        relatedVideos: [],
        pageType: ''
    });
    const detailsTimer = useTimeout();

    // Video Form state
    const videoFormShow = useShow();
    const [formData, setFormData] = useState<VideoFormData>({
        videoId: null,
        pageType: ''
    });

    // Popup Rating state
    const popupRatingShow = useShow();
    const [ratingData, setRatingData] = useState<PopupRatingData>({
        pageType: ''
    });
    const ratingTimer = useTimeout();

    // === Actions: videos list ===
    const closeVideosList = useCallback(async () => {
        await showVideos.close();
        setExpandedVideoId(-1);
    }, [showVideos, setExpandedVideoId]);

    // === Actions: video details ===
    const openVideoDetails = useCallback(async (video: TSupportVideos, relatedVideos: TSupportVideos[] = [], pageType: string = '') => {
        setDetailsData({video, relatedVideos, pageType});
        videoDetailsShow.open();
        
        // Send Google Analytics event for popup opening
        trackEvent('video_popup_opened', {
            event_category: 'support_videos',
            video_id: video.id,
            video_title: video.title,
            video_page: video.page,
            event_label: 'video id: ' + video.id
        });
        incrementVideoViews(video.id, routeType, id);
    }, [videoDetailsShow]);

    const closeVideoDetails = useCallback(() => {
        videoDetailsShow.close();
        detailsTimer.current = setTimeout(() => {
            setDetailsData({video: null, relatedVideos: [], pageType: ''});
        }, ANIMATION_DELAY);

        // Remove videoId from URL query string when closing popup
        const params = new URLSearchParams(location.search);
        if (params.has('videoId')) {
            params.delete('videoId');
            navigate({
                search: params.toString()
            }, {replace: true});
        }
    }, [videoDetailsShow, detailsTimer, navigate, location]);

    const switchVideo = useCallback((video: TSupportVideos) => {
        setDetailsData(prev => ({...prev, video}));
    }, []);

    // === Actions: edit form ===
    const openVideoForm = useCallback((videoId: number | null, pageType: string) => {
        setFormData({videoId, pageType});
        videoFormShow.open();
    }, [videoFormShow]);

    // Open edit form from video details popup
    const openFormFromDetails = useCallback(() => {
        if (!detailsData.video) return;
        setFormData({videoId: detailsData.video.id, pageType: detailsData.pageType});
        videoFormShow.open();
        closeVideoDetails();  // close video details when opening edit form
    }, [detailsData.video, detailsData.pageType, videoFormShow, closeVideoDetails]);

    const closeVideoForm = useCallback(() => {
        videoFormShow.close();
        setFormData({videoId: null, pageType: ''});
    }, [videoFormShow]);

    // === Actions: rating ===
    const openPopupRating = useCallback((pageType: string = '') => {
        setRatingData({pageType});
        popupRatingShow.open();
        closeVideosList();
    }, [popupRatingShow]);

    const closePopupRating = useCallback(() => {
        popupRatingShow.close();
        ratingTimer.current = setTimeout(() => {
            setRatingData({pageType: ''});
        }, ANIMATION_DELAY);
    }, [popupRatingShow, ratingTimer]);

    const openFormFromRating = useCallback((videoId: number) => {
        setFormData({videoId, pageType: ratingData.pageType});
        videoFormShow.open();
        closePopupRating();  // close rating popup when opening edit form
    }, [ratingData.pageType, videoFormShow, closePopupRating]);

    // === Actions: general ===
    const openContactSupport = useCallback((e?: React.MouseEvent) => {
        openWidget(e);
        closeVideosList();
        videoDetailsShow.close();
        popupRatingShow.close();
    }, [openWidget, closeVideosList, videoDetailsShow, popupRatingShow]);

    const closeAll = useCallback(() => {
        closeVideosList();
        closeVideoDetails();
        closeVideoForm();
        closePopupRating();
    }, [closeVideosList, closeVideoDetails, closeVideoForm, closePopupRating]);

    return {
        popupsShowState: {
            videosList: showVideos,
            videoDetails: {
                ...videoDetailsShow,
                data: detailsData
            },
            videoForm: {
                ...videoFormShow,
                data: formData
            },
            popupRating: {
                ...popupRatingShow,
                data: ratingData
            }
        },
        closeVideosList,
        openVideoDetails,
        closeVideoDetails,
        switchVideo,
        openVideoForm,
        openFormFromDetails,
        closeVideoForm,
        openPopupRating,
        closePopupRating,
        openFormFromRating,
        openContactSupport,
        closeAll
    };
}
